/** Shared product carousel enhancement. Cards and purchase forms remain server rendered. */
(function () {
  'use strict';

  const selector = '[data-product-carousel]';
  const instances = new Map();
  const waiting = new Set();
  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) initialize(entry.target);
      });
    }, { rootMargin: '400px 0px' })
    : null;

  function controls(root, disabled) {
    root.querySelectorAll('.product-carousel-head-actions button').forEach((button) => {
      button.disabled = disabled;
    });
  }

  function initialize(root) {
    const state = instances.get(root);
    if (!state || state.swiper || !root.isConnected) return;
    if (typeof window.Swiper !== 'function') {
      waiting.add(root);
      return;
    }

    waiting.delete(root);
    const el = state.el;
    const styles = getComputedStyle(root);
    const number = (name, fallback) => parseFloat(styles.getPropertyValue(name)) || fallback;
    const mobile = number('--spv-mobile', 2);
    const tablet = number('--spv-tablet', 4);
    const desktop = number('--spv-desktop', 6);
    const slides = Array.from(el.querySelectorAll('.swiper-slide'));

    // Preserve the nearest card if the native fallback was scrolled before enhancement.
    const bounds = el.getBoundingClientRect();
    const rtl = styles.direction === 'rtl';
    const distances = slides.map((slide) => {
      const rect = slide.getBoundingClientRect();
      return Math.abs(rtl ? bounds.right - rect.right : rect.left - bounds.left);
    });
    const initialSlide = distances.indexOf(Math.min(...distances));
    const scrollLeft = el.scrollLeft;
    const nextEl = root.querySelector('.swiper-button-next');
    const prevEl = root.querySelector('.swiper-button-prev');
    const paginationEl = root.querySelector('.swiper-pagination');
    // Loop cannot safely cross breakpoints unless there are enough cards at every size.
    const loop = el.dataset.loop === 'true' && slides.length >= Math.max(mobile, tablet, desktop) + 1;
    const options = {
      init: false,
      initialSlide,
      slidesPerView: mobile,
      spaceBetween: number('--gap-mobile', 0),
      loop,
      autoplay: el.dataset.autoplay === 'true' && !window.Shopify?.designMode
        ? { delay: Number(el.dataset.autoplayDelay) || 4000, disableOnInteraction: false }
        : false,
      breakpoints: {
        768: { slidesPerView: tablet, spaceBetween: number('--gap-tablet', 0) },
        1100: { slidesPerView: desktop, spaceBetween: number('--gap-desktop', 0) },
      },
    };
    if (nextEl && prevEl) options.navigation = { nextEl, prevEl };
    if (paginationEl) options.pagination = { el: paginationEl, clickable: true };

    try {
      el.scrollLeft = 0;
      controls(root, false);
      state.swiper = new window.Swiper(el, options);
      state.swiper.init();
      observer?.unobserve(root);
    } catch (error) {
      state.swiper?.destroy(true, true);
      state.swiper = null;
      controls(root, true);
      el.scrollLeft = scrollLeft;
      observer?.unobserve(root);
      console.warn('Product carousel: enhancement failed; using native scrolling', error);
    }
  }

  function register(root, immediate) {
    if (!instances.has(root)) {
      const el = root.querySelector('.product-carousel-swiper');
      if (!el || el.dataset.static === 'true' || !el.querySelector('.swiper-slide')) return;
      instances.set(root, { el, swiper: null });
    }
    if (immediate || !observer) initialize(root);
    else if (!instances.get(root).swiper) observer.observe(root);
  }

  function roots(scope) {
    return [ ...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector) ];
  }

  function scan(scope, immediate = false) {
    roots(scope).forEach((root) => register(root, immediate));
  }

  function destroy(root) {
    observer?.unobserve(root);
    waiting.delete(root);
    const state = instances.get(root);
    state?.swiper?.destroy(true, true);
    instances.delete(root);
    controls(root, true);
  }

  function flushWaiting() {
    waiting.forEach((root) => {
      if (root.isConnected) initialize(root);
      else destroy(root);
    });
  }

  // The layout loads Swiper before this deferred script. These events also cover a late load.
  document.querySelector('script[src*="swiper-bundle.min.js"]')?.addEventListener('load', flushWaiting, { once: true });
  window.addEventListener('load', flushWaiting, { once: true });
  document.addEventListener('shopify:section:load', (event) => scan(event.target, true));
  document.addEventListener('shopify:section:select', (event) => {
    scan(event.target, true);
    roots(event.target).forEach((root) => instances.get(root)?.swiper?.autoplay?.stop());
  });
  document.addEventListener('shopify:section:reorder', (event) => {
    scan(event.target, true);
    roots(event.target).forEach((root) => instances.get(root)?.swiper?.update());
  });
  document.addEventListener('shopify:section:unload', (event) => roots(event.target).forEach(destroy));
  ['focusin', 'pointerdown'].forEach((type) => {
    document.addEventListener(type, (event) => {
      const root = event.target.closest(selector);
      if (root) register(root, true);
    }, { passive: true });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document, !!window.Shopify?.designMode), { once: true });
  } else {
    scan(document, !!window.Shopify?.designMode);
  }
})();
